import { useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { toast } from "sonner";
import { X, Loader2, Play, Timer, Coins, Footprints, Wrench, CheckCircle2, XCircle, Zap, Trophy, History, Database, Type, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import AIMarkdown from "./AIMarkdown";
import { aiApi } from "./aiApi";

const MAX = 4;
const WANTED = ["glm-5.3", "kimi-k3", "gpt-6-astra"];
const PRESETS = [
  { label: "Kanak-kanak ikut cawangan", prompt: "Berapa jumlah kanak-kanak setiap cawangan?" },
  { label: "Cipta tugasan", prompt: "Cipta tugasan untuk penyelia Kota Bharu semak log harian esok" },
  { label: "KPI + carta", prompt: "Bandingkan KPI Kota Bharu vs Cherang dengan carta" },
  { label: "Invois tertunggak", prompt: "Berapa invois tertunggak dan jumlahnya?" },
  { label: "Pengumuman cuti", prompt: "Tulis draf pengumuman ringkas kepada ibu bapa tentang cuti Deepavali minggu depan." },
];

const fmtS = (ms) => (ms == null ? "-" : `${(ms / 1000).toFixed(1)}s`);

function ResultCard({ r, active, best, onUse, using }) {
  const running = r.status === "running";
  const tokens = (r.prompt_tokens || 0) + (r.completion_tokens || 0);
  return (
    <div className={`flex flex-col min-w-0 rounded-2xl border bg-white shadow-sm transition-shadow duration-200 hover:shadow-md ${r.ok === false ? "border-rose-200" : "border-slate-200"}`} data-testid={`compare-card-${r.model}`}>
      <div className="px-3 pt-3 pb-2 border-b border-slate-100">
        <div className="flex items-center gap-1.5">
          <div className="font-extrabold text-sm text-slate-800 truncate flex-1" title={r.model}>{r.model}</div>
          {running ? <Loader2 className="h-4 w-4 animate-spin text-fuchsia-500" /> : r.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-rose-500" />}
        </div>
        <div className="flex flex-wrap gap-1 mt-1.5 min-h-[20px]">
          {active && <Badge className="bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-100 text-[10px] px-1.5 py-0">Aktif</Badge>}
          {best.fast === r.model && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] px-1.5 py-0"><Zap className="h-3 w-3 mr-0.5" />Terpantas</Badge>}
          {best.cheap === r.model && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px] px-1.5 py-0"><Coins className="h-3 w-3 mr-0.5" />Jimat token</Badge>}
          {r.nudged && <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-500">Diingatkan sekali</Badge>}
        </div>
        {!running && (
          <div className="grid grid-cols-3 gap-1 mt-2 text-[11px]" data-testid={`compare-metrics-${r.model}`}>
            <div className="rounded-lg bg-slate-50 px-1.5 py-1"><Timer className="h-3 w-3 inline mr-0.5 text-slate-400" /><b>{fmtS(r.latency_ms)}</b></div>
            <div className="rounded-lg bg-slate-50 px-1.5 py-1" title={`Input ${r.prompt_tokens || 0} / Output ${r.completion_tokens || 0}`}><Coins className="h-3 w-3 inline mr-0.5 text-slate-400" /><b>{tokens.toLocaleString("ms-MY")}</b></div>
            <div className="rounded-lg bg-slate-50 px-1.5 py-1" title="Langkah / panggilan LLM"><Footprints className="h-3 w-3 inline mr-0.5 text-slate-400" /><b>{r.steps || 0}</b>/{r.llm_calls || 0}</div>
          </div>
        )}
        {!!(r.tools_used || []).length && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {r.tools_used.map((t, i) => <span key={i} className="text-[10px] rounded-md bg-sky-50 text-sky-700 px-1.5 py-0.5"><Wrench className="h-2.5 w-2.5 inline mr-0.5" />{t}</span>)}
          </div>
        )}
        {!!(r.proposed_actions || []).length && (
          <div className="mt-1.5 text-[11px] rounded-lg bg-emerald-50 text-emerald-700 px-2 py-1" data-testid={`compare-actions-${r.model}`}>
            Kad tindakan (simulasi): {r.proposed_actions.map((a) => a.tool).join(", ")}
          </div>
        )}
      </div>
      <div className="flex-1 px-3 py-2 overflow-y-auto max-h-[46vh] min-h-[120px]">
        {running ? (
          <div className="space-y-2 pt-1">{[80, 95, 60, 88].map((w, i) => <div key={i} className="h-2.5 rounded bg-slate-100 animate-pulse" style={{ width: `${w}%` }} />)}</div>
        ) : r.ok ? <AIMarkdown>{r.reply}</AIMarkdown> : (
          <div className="text-[12px] text-rose-700 bg-rose-50 rounded-xl p-2 break-words" data-testid={`compare-error-${r.model}`}>{r.error || "Gagal"}</div>
        )}
        {!!(r.tool_errors || []).length && <div className="mt-2 text-[10px] text-rose-600">Ralat fungsi: {r.tool_errors.join("; ")}</div>}
      </div>
      <div className="p-2 border-t border-slate-100">
        <Button size="sm" variant={active ? "outline" : "default"} disabled={running || !r.ok || active || using} onClick={() => onUse(r.model)}
          className={`w-full h-8 rounded-xl text-xs ${active ? "" : "bg-fuchsia-600 hover:bg-fuchsia-700 text-white"}`} data-testid={`compare-use-${r.model}`}>
          {using ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}{active ? "Sedang digunakan" : "Guna model ini"}
        </Button>
      </div>
    </div>
  );
}

export default function AIModelCompare({ open, onOpenChange, models, activeModel, onUse }) {
  const [picked, setPicked] = useState([]);
  const [mode, setMode] = useState("data");
  const [prompt, setPrompt] = useState(PRESETS[1].prompt);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [using, setUsing] = useState("");
  const [hist, setHist] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const poll = useRef(null);

  const options = useMemo(() => Array.from(new Set([activeModel, ...(models || [])].filter(Boolean))), [models, activeModel]);

  useEffect(() => {
    if (!open) return;
    if (!picked.length) setPicked([activeModel, ...WANTED.filter((m) => options.includes(m) || !options.length)].filter(Boolean).slice(0, MAX));
    aiApi.llmCompareHistory().then((d) => setHist(d.jobs || [])).catch(() => {});
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => () => clearInterval(poll.current), []);
  useEffect(() => {
    if (job?.status !== "running") return undefined;
    const t0 = new Date(job.created_at).getTime();
    const iv = setInterval(() => setElapsed(Math.max(0, Date.now() - t0)), 500);
    return () => clearInterval(iv);
  }, [job?.status, job?.created_at]);

  const follow = (id) => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      try {
        const j = await aiApi.llmCompareGet(id);
        setJob(j);
        if (j.status !== "running") {
          clearInterval(poll.current);
          aiApi.llmCompareHistory().then((d) => setHist(d.jobs || [])).catch(() => {});
        }
      } catch { clearInterval(poll.current); }
    }, 2000);
  };

  const toggle = (m) => setPicked((p) => (p.includes(m) ? p.filter((x) => x !== m) : p.length >= MAX ? (toast.error(`Maksimum ${MAX} model`), p) : [...p, m]));

  const start = async () => {
    if (!picked.length) return toast.error("Pilih sekurang-kurangnya 1 model");
    if (!prompt.trim()) return toast.error("Sila tulis prompt");
    setStarting(true);
    try { const j = await aiApi.llmCompare({ models: picked, prompt: prompt.trim(), mode }); setJob(j); setElapsed(0); follow(j.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal memulakan perbandingan"); } finally { setStarting(false); }
  };

  const use = async (m) => {
    setUsing(m);
    try { await onUse(m); } finally { setUsing(""); }
  };

  const results = job?.results || [];
  const done = results.filter((r) => r.status === "done" && r.ok);
  const best = {
    fast: done.length > 1 ? done.reduce((a, b) => (b.latency_ms < a.latency_ms ? b : a)).model : null,
    cheap: done.length > 1 ? done.reduce((a, b) => ((b.prompt_tokens + b.completion_tokens) < (a.prompt_tokens + a.completion_tokens) ? b : a)).model : null,
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[10020] bg-slate-900/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content aria-describedby={undefined} data-testid="ai-model-compare-dialog"
          className="fixed z-[10021] inset-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[min(96vw,1400px)] sm:h-[90vh] bg-gradient-to-b from-fuchsia-50/70 to-slate-50 sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95">
          <div className="flex items-start gap-3 px-4 sm:px-6 pt-4 pb-3 border-b border-fuchsia-100 bg-white/70">
            <div className="h-9 w-9 rounded-xl bg-fuchsia-600 text-white grid place-items-center shrink-0"><Trophy className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <DialogPrimitive.Title className="font-extrabold text-slate-800 text-base sm:text-lg">Bandingkan Model AI</DialogPrimitive.Title>
              <div className="text-[11px] sm:text-xs text-slate-500">Prompt sama dijalankan serentak pada setiap model menggunakan data sebenar. Tindakan hanya disimulasi — tiada data diubah, tiada mesej dihantar.</div>
            </div>
            <DialogPrimitive.Close className="h-8 w-8 rounded-full bg-slate-100 hover:bg-slate-200 grid place-items-center transition-colors" data-testid="compare-close"><X className="h-4 w-4" /></DialogPrimitive.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
            <div className="grid lg:grid-cols-[1fr_1.3fr] gap-4">
              <div className="rounded-2xl bg-white border border-slate-200 p-3 space-y-2.5">
                <div className="text-[11px] font-bold text-slate-500">Model ({picked.length}/{MAX})</div>
                <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto" data-testid="compare-model-chips">
                  {options.map((m) => {
                    const on = picked.includes(m);
                    return (
                      <button key={m} type="button" onClick={() => toggle(m)} data-testid={`compare-chip-${m}`}
                        className={`text-xs rounded-full px-2.5 py-1 border transition-colors duration-150 ${on ? "bg-fuchsia-600 border-fuchsia-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-fuchsia-300 hover:text-fuchsia-700"}`}>
                        {m}{m === activeModel ? " · aktif" : ""}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[11px] font-bold text-slate-500 pt-1">Mod ujian</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {[["data", Database, "Data sebenar + fungsi"], ["text", Type, "Teks sahaja"]].map(([k, Ic, label]) => (
                    <button key={k} type="button" onClick={() => setMode(k)} data-testid={`compare-mode-${k}`}
                      className={`flex items-center justify-center gap-1.5 text-xs rounded-xl px-2 py-2 border transition-colors duration-150 ${mode === k ? "bg-fuchsia-50 border-fuchsia-300 text-fuchsia-700 font-bold" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                      <Ic className="h-3.5 w-3.5" />{label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl bg-white border border-slate-200 p-3 space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {PRESETS.map((p) => (
                    <button key={p.label} type="button" onClick={() => setPrompt(p.prompt)} data-testid={`compare-preset-${p.label}`}
                      className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors duration-150 ${prompt === p.prompt ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-amber-50"}`}>{p.label}</button>
                  ))}
                </div>
                <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} maxLength={2000} className="text-sm bg-white resize-none" placeholder="Tulis soalan untuk Mimi Junior…" data-testid="compare-prompt" />
                <div className="flex items-center gap-2">
                  <div className="text-[11px] text-slate-400 flex-1">{job?.status === "running" ? `Berjalan… ${fmtS(elapsed)}` : "Hasil muncul pada setiap kad sebaik model selesai."}</div>
                  <Button onClick={start} disabled={starting || job?.status === "running"} className="h-9 rounded-xl bg-fuchsia-600 hover:bg-fuchsia-700 text-white px-4" data-testid="compare-start-btn">
                    {starting || job?.status === "running" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Play className="h-4 w-4 mr-1.5" />} Mula Bandingkan
                  </Button>
                </div>
              </div>
            </div>

            {job ? (
              <div>
                <div className="text-[11px] text-slate-500 mb-2">Prompt: <span className="text-slate-700 font-semibold">“{job.prompt}”</span> · {job.mode === "text" ? "Teks sahaja" : "Data sebenar"}</div>
                <div className={`grid gap-3 grid-cols-1 sm:grid-cols-2 ${results.length >= 4 ? "xl:grid-cols-4" : results.length === 3 ? "xl:grid-cols-3" : ""}`} data-testid="compare-results">
                  {results.map((r) => <ResultCard key={r.model} r={r} active={r.model === activeModel} best={best} onUse={use} using={using === r.model} />)}
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-fuchsia-200 bg-white/60 p-8 text-center text-sm text-slate-500">Pilih model & prompt, kemudian tekan <b>Mula Bandingkan</b>.</div>
            )}

            {!!hist.length && (
              <div className="rounded-2xl bg-white border border-slate-200 p-3">
                <div className="text-[11px] font-bold text-slate-500 mb-1.5 flex items-center gap-1"><History className="h-3.5 w-3.5" /> Perbandingan lepas</div>
                <div className="divide-y divide-slate-100" data-testid="compare-history">
                  {hist.map((h) => (
                    <button key={h.id} type="button" onClick={() => { setJob(h); if (h.status === "running") follow(h.id); }}
                      className="w-full text-left py-1.5 text-xs hover:bg-slate-50 rounded-lg px-1.5 flex gap-2 transition-colors">
                      <span className="text-slate-400 shrink-0">{new Date(h.created_at).toLocaleString("ms-MY", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      <span className="truncate flex-1 text-slate-700">{h.prompt}</span>
                      <span className="text-slate-400 shrink-0 hidden sm:inline">{(h.models || []).join(", ")}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
